import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Users, Plus, Mail, Calendar, ChevronRight, Search, KeyRound } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import Pagination from '../../components/ui/Pagination';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 15;

export default function AdminClients() {
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', first_name: '', last_name: '' });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [resetSending, setResetSending] = useState(null);

  const sendPasswordReset = async (e, client) => {
    e.preventDefault();
    e.stopPropagation();
    const label = client.billing_info
      ? `${client.billing_info.first_name} ${client.billing_info.last_name}`.trim() || client.email
      : client.email;
    if (!window.confirm(`Envoyer un lien de réinitialisation de mot de passe à ${label} (${client.email}) ?`)) {
      return;
    }
    setResetSending(client.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${client.id}/send-password-reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success(data.message || 'Email envoyé');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setResetSending(null);
    }
  };

  // Filter clients based on search query
  const filteredClients = useMemo(() => {
    if (!searchQuery.trim()) return clients;
    const query = searchQuery.toLowerCase();
    return clients.filter(client => 
      client.email?.toLowerCase().includes(query) ||
      client.first_name?.toLowerCase().includes(query) ||
      client.last_name?.toLowerCase().includes(query) ||
      `${client.first_name} ${client.last_name}`.toLowerCase().includes(query)
    );
  }, [clients, searchQuery]);

  const paginatedClients = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredClients.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredClients, currentPage]);

  const totalPages = Math.ceil(filteredClients.length / ITEMS_PER_PAGE);

  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setClients(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/clients`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Erreur');
      }
      
      setShowCreateForm(false);
      setFormData({ email: '', password: '', first_name: '', last_name: '' });
      fetchClients();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse h-64 bg-muted rounded-xl" />;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2">Clients</h1>
          <p className="text-muted-foreground">{clients.length} client(s) enregistré(s)</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="btn-primary">
          <Plus className="mr-2 h-4 w-4" /> Nouveau client
        </Button>
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>Créer un compte client</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-4">
              {error && (
                <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">{error}</div>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  placeholder="Prénom"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                />
                <Input
                  placeholder="Nom"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                />
              </div>
              <Input
                type="email"
                placeholder="Email *"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
              <Input
                type="password"
                placeholder="Mot de passe *"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
              />
              <div className="flex gap-2">
                <Button type="submit" disabled={creating} className="btn-primary">
                  {creating ? 'Création...' : 'Créer le client'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Clients List */}
      <Card>
        <CardContent className="p-0">
          {/* Search bar */}
          <div className="p-4 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom ou email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            {searchQuery && (
              <p className="text-sm text-muted-foreground mt-2">
                {filteredClients.length} résultat{filteredClients.length > 1 ? 's' : ''} pour "{searchQuery}"
              </p>
            )}
          </div>
          {clients.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucun client pour le moment</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {paginatedClients.map((client) => (
                <Link 
                  key={client.id} 
                  to={`/admin/clients/${client.id}`}
                  className="block p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-semibold">
                          {client.email[0].toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">
                          {client.billing_info 
                            ? `${client.billing_info.first_name} ${client.billing_info.last_name}`.trim() || client.email
                            : client.email
                          }
                        </p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" /> {client.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> 
                            {new Date(client.created_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={(e) => sendPasswordReset(e, client)}
                        disabled={resetSending === client.id}
                        className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                        title="Envoyer un lien de réinitialisation de mot de passe"
                        data-testid={`reset-pwd-${client.id}`}
                      >
                        <KeyRound className={`h-4 w-4 ${resetSending === client.id ? 'animate-pulse' : ''}`} />
                      </button>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        client.is_active 
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {client.is_active ? 'Actif' : 'Inactif'}
                      </span>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredClients.length}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </CardContent>
      </Card>
    </div>
  );
}
